import Typewriter from 'typewriter-effect';
import { m, useScroll } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useResponsive } from 'src/hooks/use-responsive';

import { HEADER } from 'src/layouts/config-layout';
import { bgBlur, bgGradient, textGradient } from 'src/theme/css';

import Iconify from 'src/components/iconify';
import { varFade, MotionContainer } from 'src/components/animate';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  ...bgGradient({
    color: alpha(theme.palette.background.default, theme.palette.mode === 'light' ? 0.9 : 0.94),
    imgUrl: '/assets/background/overlay_3.jpg',
  }),
  width: '100%',
  height: '100vh',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    top: 0,
    left: 0,
    position: 'fixed',
  },
}));

const StyledWrapper = styled('div')(({ theme }) => ({
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    marginTop: HEADER.H_DESKTOP_OFFSET,
  },
}));

const StyledTextGradient = styled(m.h1)(({ theme }) => ({
  ...textGradient(
    `300deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 25%, ${theme.palette.primary.main} 50%, ${theme.palette.warning.main} 75%, ${theme.palette.primary.main} 100%`
  ),
  padding: 0,
  marginTop: 8,
  lineHeight: 1.1,
  fontWeight: 900,
  marginBottom: 24,
  letterSpacing: 2,
  backgroundSize: '400%',
  fontSize: '1.8rem',
  fontFamily: theme.typography.fontSecondaryFamily,
  [theme.breakpoints.up('sm')]: {
    fontSize: '2rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '2.4rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '2.8rem',
  },
}));

const StyledPolygon = styled('div')(({ opacity = 1, anchor = 'left', theme }) => ({
  ...bgBlur({
    opacity,
    color: theme.palette.background.default,
  }),
  zIndex: 9,
  bottom: 0,
  height: 80,
  width: '50%',
  position: 'absolute',
  clipPath: 'polygon(0% 0%, 100% 100%, 0% 100%)',
  ...(anchor === 'left' && {
    left: 0,
  }),
  ...(anchor === 'right' && {
    right: 0,
    transform: 'scaleX(-1)',
  }),
}));

// ----------------------------------------------------------------------
export default function HomeHero() {
  const mdUp = useResponsive('up', 'md');
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const [percent, setPercent] = useState(0);
  useEffect(() => {
    if (!heroRef.current) {
      return () => {};
    }
    const unsubscribe = scrollY.on('change', (scrollHeight) => {
      const heroHeight = heroRef.current?.offsetHeight;
      if (!heroHeight) return;
      const scrollPercent = (scrollHeight * 100) / heroHeight;
      setPercent(Math.floor(scrollPercent));
    });

    return () => {
      unsubscribe();
    };
  }, [scrollY]);

  const opacity = 1 - percent / 100;
  const hide = percent > 120;
  const renderDescription = (
    <Stack
      flexGrow={1}
      alignItems={{ xs: 'left', md: 'flex-start' }}
      sx={{
        textAlign: { xs: 'left', md: 'flex-start' },
        height: 1,
        mx: 'auto',
        opacity: opacity > 0 ? opacity : 0,
        mt: { md: 5, xs: 10 },
      }}
    >
      <m.div variants={varFade().in}>
        <Typography variant="h6">Welcome to My Digital Workspace!</Typography>
      </m.div>
      <m.div variants={varFade().in}>
        <StyledTextGradient
          animate={{ backgroundPosition: '200% center' }}
          transition={{
            repeatType: 'reverse',
            ease: 'linear',
            duration: 20,
            repeat: Infinity,
          }}
        >
          Hi, I&apos;m Satyendra Singh 👋
        </StyledTextGradient>
      </m.div>
      <m.div variants={varFade().in}>
        <Typography variant="body2">
          Senior Software Engineer with 4+ years of experience building scalable, high-performance
          web applications using React.js and Node.js. Proven at crafting clean, maintainable UI
          architectures and integrating AI & NLP-driven systems to ship reliable, user-focused
          digital products. 🚀
        </Typography>
      </m.div>
      <Stack spacing={0.75} direction="row" alignItems="center" sx={{ my: 3 }}>
        <m.div variants={varFade().in}>
          <Typography component="h2" variant="subtitle2" sx={{ mr: 0.5 }}>
            Core Capabilities
          </Typography>
        </m.div>

        <Typography component="h2" variant="subtitle2">
          <Typewriter
            options={{
              strings: [
                'React & Next.js',
                'Scalable UI Architecture',
                'AI / NLP Integrations',
                'API-Driven Systems',
                'Secure Deployments',
                'User-First UX',
              ],
              autoStart: true,
              loop: true,
              delay: 80,
              deleteSpeed: 80,
            }}
          />
        </Typography>
      </Stack>

      <m.div variants={varFade().in}>
        <Stack spacing={3} direction={{ xs: 'column-reverse', sm: 'row' }} sx={{ mb: 2 }}>
          <Button
            href={paths.post.root}
            color="inherit"
            size="large"
            variant="outlined"
            startIcon={<Iconify icon="mdi:rocket" width={24} />}
            rel="noopener"
            sx={{ borderColor: 'currentColor' }}
          >
            View Projects
          </Button>
          <Button
            component={RouterLink}
            href="https://www.linkedin.com/in/satyendraintech"
            color="info"
            size="large"
            variant="contained"
            startIcon={<Iconify icon="simple-icons:linkedin" width={24} />}
          >
            Connect on LinkedIn
          </Button>
        </Stack>
      </m.div>

      <Stack spacing={3}>
        <m.div variants={varFade().in}>
          <Typography variant="overline" sx={{ opacity: 0.48, mb: 0.5 }}>
            Core tools I use daily
          </Typography>
        </m.div>
        <Stack spacing={3} direction="row" alignItems="center" flexWrap="wrap">
          {[
            'html',
            'javascript',
            'reactdark',
            'nextjsdark',
            'redux',
            'tailwinddark',
            'nodejsdark',
            'mongodb',
            'figmadark',
          ].map((icon) => (
            <m.div key={icon} variants={varFade().in}>
              <Box
                component={m.img}
                alt={icon}
                src={`/assets/icons/skills/ic_${icon}.svg`}
                sx={{
                  width: 24,
                  height: 24,
                  opacity: 0.85,
                  transition: 'transform 0.3s ease, opacity 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.15)',
                    opacity: 1,
                  },
                }}
              />
            </m.div>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );

  const renderSlides = (
    <Stack
      component={m.div}
      sx={{
        position: 'relative',
        mt: 5,
        perspective: 1000,
      }}
    >
      <Box
        component="img"
        alt="Satyendra Singh"
        src="/assets/images/myphoto/satyendra_singh.jpeg"
        sx={{
          position: 'absolute',
          width: '100%',
          maxWidth: 300,
          borderRadius: 2,

          transform: 'rotateY(-10deg) rotateX(14deg) scale(1.03)',
          filter: 'contrast(1.15) saturate(1.2) brightness(1.05)',

          boxShadow: '0 24px 48px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.06)',

          transition:
            'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1), filter 0.5s ease, box-shadow 0.5s ease',

          '&:hover': {
            transform: 'rotateY(0deg) rotateX(0deg) scale(1)',
            filter: 'none',
            boxShadow: '0 12px 30px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.08)',
          },
        }}
      />
    </Stack>
  );

  const renderPolygons = (
    <>
      <StyledPolygon />
      <StyledPolygon anchor="right" opacity={0.48} />
      <StyledPolygon anchor="right" opacity={0.48} sx={{ height: 48, zIndex: 10 }} />
      <StyledPolygon anchor="right" sx={{ zIndex: 11, height: 24 }} />
    </>
  );

  return (
    <>
      <StyledRoot
        ref={heroRef}
        sx={{
          ...(hide && {
            opacity: 0,
          }),
        }}
      >
        <StyledWrapper>
          <Container component={MotionContainer} sx={{ height: 1 }}>
            <Grid container columnSpacing={{ md: 10 }} sx={{ height: 1 }}>
              <Grid xs={12} md={8} lg={8}>
                {renderDescription}
              </Grid>
              {mdUp && (
                <Grid md={4} lg={4}>
                  {renderSlides}
                </Grid>
              )}
            </Grid>
          </Container>
        </StyledWrapper>
      </StyledRoot>

      {mdUp && renderPolygons}

      <Box sx={{ height: { md: '100vh' } }} />
    </>
  );
}
